/**
 * The public-page link sets, in one place so the marketing header and footer
 * stay in step — add a page here and both pick it up.
 *
 * `to` is written with no language prefix; `Link` renders the href in whatever
 * language the visitor is in.
 */
import type { MessageKey } from "@/i18n";

export interface NavLink {
  to: string;
  label: MessageKey;
}

/** "Why you need it" — one page per reason the certificate is required. */
export const USE_CASE_LINKS: NavLink[] = [
  { to: "/karta-pobytu", label: "nav.forResidence" },
  { to: "/obywatelstwo", label: "nav.forCitizenship" },
  { to: "/studia", label: "nav.forStudies" },
];

export const COMPARE_LINK: NavLink = { to: "/porownanie", label: "nav.compare" };

/**
 * One page per exam, and the page that chooses between them. All three are flat
 * links in the header, side by side: it is where a reader finds out there is a
 * choice at all, and hiding any of them behind a dropdown would be making it for
 * them. The comparison used to sit in the "why you need it" dropdown, which put
 * the site's one decision page two clicks from every page that leads to it.
 */
export const EXAM_LINKS: NavLink[] = [
  { to: "/telc", label: "nav.examInfo" },
  { to: "/certyfikat", label: "nav.certyfikatInfo" },
  COMPARE_LINK,
];

export interface NavGroup {
  label: MessageKey;
  links: NavLink[];
}

/** The header's exam → task group → practical guides hierarchy. */
export const EXAM_MENUS: { label: MessageKey; groups: NavGroup[] }[] = [
  {
    label: "nav.examInfo",
    groups: [
      { label: "nav.examOverview", links: [{ to: "/telc", label: "nav.examInfo" }, { to: "/telc/zasady", label: "nav.telcRules" }] },
      { label: "nav.examTasksGroup", links: [
        { to: "/telc/zadania", label: "nav.telcTasks" },
        { to: "/telc/list", label: "nav.telcLetterGuide" },
        { to: "/telc/opis-zdjecia", label: "nav.telcPictureGuide" },
        { to: "/telc/prezentacja", label: "nav.telcPresentationGuide" },
      ] },
    ],
  },
  {
    label: "nav.certyfikatInfo",
    groups: [
      { label: "nav.examOverview", links: [
        { to: "/certyfikat", label: "nav.certyfikatInfo" },
        { to: "/certyfikat/zasady", label: "nav.certyfikatRules" },
        { to: "/certyfikat/b1", label: "nav.certyfikatB1" },
        { to: "/certyfikat/b2", label: "nav.certyfikatB2" },
      ] },
      { label: "nav.examTasksGroup", links: [
        { to: "/certyfikat/zadania", label: "nav.certyfikatTasks" },
        { to: "/certyfikat/pisanie", label: "nav.certyfikatWritingGuide" },
        { to: "/certyfikat/mowienie-b1", label: "nav.certyfikatSpeakingB1" },
        { to: "/certyfikat/mowienie-b2", label: "nav.certyfikatSpeakingB2" },
      ] },
    ],
  },
];

/** Every telc guide in the footer — one column per exam, so neither grows into the other. */
export const TELC_COLUMN_LINKS: NavLink[] = [
  { to: "/telc", label: "nav.examInfo" },
  { to: "/telc/zasady", label: "nav.telcRules" },
  { to: "/telc/zadania", label: "nav.telcTasks" },
  { to: "/telc/list", label: "nav.telcLetterGuide" },
  { to: "/telc/opis-zdjecia", label: "nav.telcPictureGuide" },
  { to: "/telc/prezentacja", label: "nav.telcPresentationGuide" },
];

/** Every state-exam guide in the footer. */
export const STATE_COLUMN_LINKS: NavLink[] = [
  { to: "/certyfikat", label: "nav.certyfikatInfo" },
  { to: "/certyfikat/b1", label: "nav.certyfikatB1" },
  { to: "/certyfikat/b2", label: "nav.certyfikatB2" },
  { to: "/certyfikat/zadania", label: "nav.certyfikatTasks" },
  { to: "/certyfikat/zasady", label: "nav.certyfikatRules" },
  { to: "/certyfikat/pisanie", label: "nav.certyfikatWritingGuide" },
  { to: "/certyfikat/mowienie-b1", label: "nav.certyfikatSpeakingB1" },
  { to: "/certyfikat/mowienie-b2", label: "nav.certyfikatSpeakingB2" },
];

/**
 * What a reader deals with before the exam itself: when they can sit it, what
 * they can practise on, and what this costs. Three different questions, none of
 * them about a particular exam — which is why they are their own column rather
 * than repeated under both.
 */
export const BEFORE_LINKS: NavLink[] = [
  { to: "/terminy", label: "nav.cycle" },
  { to: "/arkusze", label: "nav.materials" },
  { to: "/pisanie", label: "nav.writingGuide" },
  { to: "/slowka", label: "nav.vocabulary" },
  { to: "/poradnik", label: "nav.blog" },
  { to: "/pricing", label: "nav.pricing" },
];

/** For a school or a centre — in the footer's last column, not in the header: the reader is a buyer, not a candidate. */
export const SCHOOL_LINKS: NavLink[] = [
  { to: "/dla-szkol", label: "nav.schools" },
  { to: "/dla-korepetytorow", label: "nav.tutors" },
];

export const LEGAL_LINKS: NavLink[] = [
  { to: "/terms", label: "legal.terms.title" },
  { to: "/privacy", label: "legal.privacy.title" },
  { to: "/cookies", label: "legal.cookies.title" },
  { to: "/kontakt", label: "contact.title" },
];

/**
 * Our accounts, where the short videos go out. External, so plain `href`s
 * rather than `NavLink`s, and brand names rather than catalogue keys — they
 * read the same in every language.
 */
export const SOCIAL_LINKS: { href: string; label: string }[] = [
  { href: "https://www.instagram.com/polskiegzamin.pl/", label: "Instagram" },
  { href: "https://www.youtube.com/@polskiegzamin", label: "YouTube" },
  { href: "https://www.tiktok.com/@polskiegzamin.pl", label: "TikTok" },
];

/** The compact footer's one wrapped row, for the screens behind sign-in. */
export const COMPACT_LINKS: NavLink[] = [...EXAM_LINKS, ...BEFORE_LINKS];
